import React, { useEffect, useState, createElement } from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { InfoIcon, HelpCircleIcon, EyeIcon, SaveIcon, DownloadIcon, RefreshCwIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronRightIcon, FileTextIcon, ToggleLeftIcon, ToggleRightIcon, DollarSignIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
// EMVCo Tag interfaces
interface Tag {
  id: string;
  name: string;
  value: string;
  description: string;
  mandatory: boolean;
  editable: boolean;
  enabled: boolean;
  maxLength?: number;
  validation?: RegExp;
}
interface OptionalTag extends Tag {
  enabled: boolean;
}
interface AdditionalDataField {
  id: string;
  name: string;
  value: string;
  enabled: boolean;
}
const QRGenerator: React.FC = () => {
  // Merchant information
  const [merchantInfo, setMerchantInfo] = useState({
    merchantId: 'MERCH12345',
    merchantName: 'ABC Corporation',
    tillId: 'TILL001',
    mcc: '5411' // Grocery stores, supermarkets
  });
  // Static amount configuration
  const [includeAmount, setIncludeAmount] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('PKR');
  // Mandatory tags (auto-filled)
  const [mandatoryAutoTags, setMandatoryAutoTags] = useState<Tag[]>([{
    id: '00',
    name: 'Payload Format Indicator',
    value: '01',
    description: 'The version of the QR Code template',
    mandatory: true,
    editable: false,
    enabled: true
  }, {
    id: '01',
    name: 'Point of Initiation Method',
    value: '11',
    description: 'Static QR Code',
    mandatory: true,
    editable: false,
    enabled: true
  }, {
    id: '52',
    name: 'Merchant Category Code',
    value: '5411',
    description: 'MCC for merchant type',
    mandatory: true,
    editable: false,
    enabled: true
  }]);
  // Mandatory tags (user input required)
  const [mandatoryUserTags, setMandatoryUserTags] = useState<Tag[]>([{
    id: '59',
    name: 'Merchant Name',
    value: 'ABC Corporation',
    description: 'Name of the merchant',
    mandatory: true,
    editable: true,
    enabled: true,
    maxLength: 25
  }, {
    id: '60',
    name: 'Merchant City',
    value: 'Karachi',
    description: 'City of the merchant',
    mandatory: true,
    editable: true,
    enabled: true,
    maxLength: 15
  }]);
  // Optional tags
  const [optionalTags, setOptionalTags] = useState<OptionalTag[]>([{
    id: '58',
    name: 'Country Code',
    value: 'PK',
    description: 'Country code (ISO 3166-1 alpha 2)',
    mandatory: false,
    editable: true,
    enabled: true,
    maxLength: 2
  }, {
    id: '54',
    name: 'Transaction Amount',
    value: '',
    description: 'Transaction amount',
    mandatory: false,
    editable: true,
    enabled: false
  }, {
    id: '53',
    name: 'Transaction Currency',
    value: '586',
    description: 'ISO 4217 currency code',
    mandatory: false,
    editable: true,
    enabled: false
  }]);
  // Additional data fields (Tag 62)
  const [additionalDataFields, setAdditionalDataFields] = useState<AdditionalDataField[]>([{
    id: '01',
    name: 'Bill Number',
    value: '',
    enabled: false
  }, {
    id: '05',
    name: 'Reference Label',
    value: '',
    enabled: false
  }, {
    id: '07',
    name: 'Terminal Label',
    value: '',
    enabled: false
  }]);
  // CRC (Tag 63)
  const [crc, setCrc] = useState('6403');
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    merchantInfo: true,
    mandatoryAuto: true,
    mandatoryUser: true,
    optional: true,
    additionalData: false
  });
  // QR code value
  const [qrCodeValue, setQrCodeValue] = useState('');
  const [qrError, setQrError] = useState('');
  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Handle optional tag toggle
  const toggleOptionalTag = (tagId: string) => {
    setOptionalTags(optionalTags.map(tag => tag.id === tagId ? {
      ...tag,
      enabled: !tag.enabled
    } : tag));
  };
  // Handle additional data field toggle
  const toggleAdditionalDataField = (fieldId: string) => {
    setAdditionalDataFields(additionalDataFields.map(field => field.id === fieldId ? {
      ...field,
      enabled: !field.enabled
    } : field));
  };
  // Handle mandatory user tag change
  const handleMandatoryTagChange = (tagId: string, value: string) => {
    setMandatoryUserTags(mandatoryUserTags.map(tag => tag.id === tagId ? {
      ...tag,
      value
    } : tag));
  };
  // Handle optional tag change
  const handleOptionalTagChange = (tagId: string, value: string) => {
    setOptionalTags(optionalTags.map(tag => tag.id === tagId ? {
      ...tag,
      value
    } : tag));
  };
  // Handle additional data field change
  const handleAdditionalDataFieldChange = (fieldId: string, value: string) => {
    setAdditionalDataFields(additionalDataFields.map(field => field.id === fieldId ? {
      ...field,
      value
    } : field));
  };
  // Toggle include amount
  const handleToggleAmount = () => {
    const newIncludeAmount = !includeAmount;
    setIncludeAmount(newIncludeAmount);
    // Update optional tags 54 and 53 based on include amount toggle
    setOptionalTags(optionalTags.map(tag => tag.id === '54' || tag.id === '53' ? {
      ...tag,
      enabled: newIncludeAmount
    } : tag));
    // Update tag 54 value when amount changes
    if (newIncludeAmount) {
      setOptionalTags(optionalTags.map(tag => tag.id === '54' ? {
        ...tag,
        value: amount
      } : tag));
    }
  };
  // Handle amount change
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    // Update tag 54 value when amount changes
    setOptionalTags(optionalTags.map(tag => tag.id === '54' ? {
      ...tag,
      value: newAmount
    } : tag));
  };
  // Handle currency change
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value;
    setCurrency(newCurrency);
    // Map currency to ISO 4217 code
    const currencyCodeMap: {
      [key: string]: string;
    } = {
      PKR: '586',
      USD: '840',
      EUR: '978',
      GBP: '826'
    };
    // Update tag 53 value when currency changes
    setOptionalTags(optionalTags.map(tag => tag.id === '53' ? {
      ...tag,
      value: currencyCodeMap[newCurrency] || '586'
    } : tag));
  };
  // Generate QR code value
  useEffect(() => {
    try {
      let qrValue = '';
      // Add mandatory auto tags
      mandatoryAutoTags.forEach(tag => {
        if (tag.value) {
          qrValue += `${tag.id}${tag.value.length.toString().padStart(2, '0')}${tag.value}`;
        }
      });
      // Add mandatory user tags
      mandatoryUserTags.forEach(tag => {
        if (tag.value) {
          qrValue += `${tag.id}${tag.value.length.toString().padStart(2, '0')}${tag.value}`;
        }
      });
      // Add enabled optional tags
      optionalTags.forEach(tag => {
        if (tag.enabled && tag.value) {
          qrValue += `${tag.id}${tag.value.length.toString().padStart(2, '0')}${tag.value}`;
        }
      });
      // Add additional data fields (Tag 62)
      const enabledAdditionalFields = additionalDataFields.filter(field => field.enabled && field.value);
      if (enabledAdditionalFields.length > 0) {
        let additionalData = '';
        enabledAdditionalFields.forEach(field => {
          additionalData += `${field.id}${field.value.length.toString().padStart(2, '0')}${field.value}`;
        });
        qrValue += `62${additionalData.length.toString().padStart(2, '0')}${additionalData}`;
      }
      // Add CRC (placeholder - in a real implementation, this would be calculated)
      qrValue += `6304${crc}`;
      setQrCodeValue(qrValue);
      setQrError('');
    } catch (error) {
      console.error('Error generating QR code:', error);
      setQrError('Error generating QR code');
    }
  }, [mandatoryAutoTags, mandatoryUserTags, optionalTags, additionalDataFields, crc]);
  // Download QR code
  const downloadQR = () => {
    const canvas = document.getElementById('qr-code') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${merchantInfo.merchantName.replace(/\s+/g, '_')}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  // Download metadata
  const downloadMetadata = () => {
    const metadata = {
      merchantInfo,
      mandatoryAutoTags,
      mandatoryUserTags,
      optionalTags: optionalTags.filter(tag => tag.enabled),
      additionalDataFields: additionalDataFields.filter(field => field.enabled),
      qrCodeValue
    };
    const dataStr = JSON.stringify(metadata, null, 2);
    const blob = new Blob([dataStr], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${merchantInfo.merchantName.replace(/\s+/g, '_')}_QR_Metadata.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-4900">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-4901">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4902">
          EMVCo QR Code Generator
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-3" data-id="element-4903">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => {
          // Reset form
          setIncludeAmount(false);
          setAmount('');
          setCurrency('PKR');
          setMandatoryUserTags(mandatoryUserTags.map(tag => ({
            ...tag,
            value: tag.id === '59' ? merchantInfo.merchantName : tag.id === '60' ? 'Karachi' : ''
          })));
          setOptionalTags(optionalTags.map(tag => ({
            ...tag,
            value: tag.id === '58' ? 'PK' : '',
            enabled: tag.id === '58'
          })));
          setAdditionalDataFields(additionalDataFields.map(field => ({
            ...field,
            value: '',
            enabled: false
          })));
        }} data-id="element-4904">
            <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-4905" />
            Reset
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={() => {
          // Save QR code
          alert('QR Code saved to merchant profile');
        }} data-id="element-4906">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-4907" />
            Save QR Code
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4908">
        <div className="lg:col-span-2 space-y-6" data-id="element-4909">
          {/* Section 1: QRC Setup Configuration */}
          <Card data-id="element-4910">
            <div className="flex justify-between items-center mb-4" data-id="element-4911">
              <h2 className="text-lg font-medium" data-id="element-4912">QRC Setup Configuration</h2>
              <button onClick={() => toggleSection('merchantInfo')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-4913">
                {expandedSections.merchantInfo ? <ChevronDownIcon className="h-5 w-5" data-id="element-4914" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-4915" />}
              </button>
            </div>
            {expandedSections.merchantInfo && <>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md mb-4" data-id="element-4916">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4917">
                    Merchant Information (Pre-filled)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4918">
                    <div data-id="element-4919">
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4920">
                        Merchant ID
                      </label>
                      <input type="text" value={merchantInfo.merchantId} readOnly className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm" data-id="element-4921" />
                    </div>
                    <div data-id="element-4922">
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4923">
                        Merchant Name
                      </label>
                      <input type="text" value={merchantInfo.merchantName} readOnly className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm" data-id="element-4924" />
                    </div>
                    <div data-id="element-4925">
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4926">
                        Till ID
                      </label>
                      <select value={merchantInfo.tillId} onChange={e => setMerchantInfo({
                    ...merchantInfo,
                    tillId: e.target.value
                  })} className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm" data-id="element-4927">
                        <option value="TILL001" data-id="element-4928">TILL001</option>
                        <option value="TILL002" data-id="element-4929">TILL002</option>
                        <option value="TILL003" data-id="element-4930">TILL003</option>
                      </select>
                    </div>
                    <div data-id="element-4931">
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4932">
                        Business Category (MCC)
                      </label>
                      <input type="text" value={merchantInfo.mcc} readOnly className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm" data-id="element-4933" />
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4934">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4935">
                    Static/Fixed Amount Configuration
                  </h3>
                  <div className="flex items-center mb-4" data-id="element-4936">
                    <button type="button" className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handleToggleAmount} style={{
                  backgroundColor: includeAmount ? '#3B82F6' : '#E5E7EB'
                }} data-id="element-4937">
                      <span className={`${includeAmount ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`} data-id="element-4938"></span>
                    </button>
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4939">
                      Include amount in QR
                    </span>
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-1 cursor-help" title="When enabled, a fixed amount will be included in the QR code" data-id="element-4940" />
                  </div>
                  {includeAmount && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4941">
                      <div data-id="element-4942">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4943">
                          Amount
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm" data-id="element-4944">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4945">
                            <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-4946" />
                          </div>
                          <input type="text" value={amount} onChange={handleAmountChange} placeholder="0.00" className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-md" data-id="element-4947" />
                        </div>
                      </div>
                      <div data-id="element-4948">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4949">
                          Currency
                        </label>
                        <select value={currency} onChange={handleCurrencyChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-4950">
                          <option value="PKR" data-id="element-4951">PKR - Pakistani Rupee</option>
                          <option value="USD" data-id="element-4952">USD - US Dollar</option>
                          <option value="EUR" data-id="element-4953">EUR - Euro</option>
                          <option value="GBP" data-id="element-4954">GBP - British Pound</option>
                        </select>
                      </div>
                    </div>}
                </div>
              </>}
          </Card>
          {/* Section 2: Tag Management Interface */}
          <Card data-id="element-4955">
            <h2 className="text-lg font-medium mb-4" data-id="element-4956">
              Tag Management Interface
            </h2>
            {/* A. Mandatory Tags – Auto-Prefilled */}
            <div className="mb-6" data-id="element-4957">
              <div className="flex justify-between items-center mb-2" data-id="element-4958">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4959">
                  A. Mandatory Tags – Auto-Prefilled (Read-Only)
                </h3>
                <button onClick={() => toggleSection('mandatoryAuto')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-4960">
                  {expandedSections.mandatoryAuto ? <ChevronDownIcon className="h-5 w-5" data-id="element-4961" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-4962" />}
                </button>
              </div>
              {expandedSections.mandatoryAuto && <div className="bg-gray-50 dark:bg-gray-800 rounded-md overflow-hidden" data-id="element-4963">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4964">
                    <thead className="bg-gray-100 dark:bg-gray-700" data-id="element-4965">
                      <tr data-id="element-4966">
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4967">
                          Tag ID
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4968">
                          Name
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4969">
                          Value
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4970">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4971">
                      {mandatoryAutoTags.map(tag => <tr key={tag.id} data-id="element-4972">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-4973">
                            {tag.id}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4974">
                            {tag.name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4975">
                            {tag.value}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4976">
                            {tag.description}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
            {/* B. Mandatory Tags – User Input Required */}
            <div className="mb-6" data-id="element-4977">
              <div className="flex justify-between items-center mb-2" data-id="element-4978">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4979">
                  B. Mandatory Tags – User Input Required
                </h3>
                <button onClick={() => toggleSection('mandatoryUser')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-4980">
                  {expandedSections.mandatoryUser ? <ChevronDownIcon className="h-5 w-5" data-id="element-4981" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-4982" />}
                </button>
              </div>
              {expandedSections.mandatoryUser && <div className="space-y-4" data-id="element-4983">
                  {mandatoryUserTags.map(tag => <div key={tag.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4" data-id="element-4984">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-4985">
                        <div className="mb-2 md:mb-0" data-id="element-4986">
                          <div className="flex items-center" data-id="element-4987">
                            <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2" data-id="element-4988">
                              Tag {tag.id}
                            </span>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4989">
                              {tag.name}
                            </h4>
                            <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-1 cursor-help" title={tag.description} data-id="element-4990" />
                          </div>
                        </div>
                        <div className="flex-1 md:ml-4 max-w-md" data-id="element-4991">
                          <input type="text" value={tag.value} onChange={e => handleMandatoryTagChange(tag.id, e.target.value)} className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-800" placeholder={`Enter ${tag.name}`} maxLength={tag.maxLength} required data-id="element-4992" />
                          {tag.maxLength && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4993">
                              Maximum {tag.maxLength} characters
                            </p>}
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>
            {/* C. Optional Tags – Toggle-Based Configuration */}
            <div className="mb-6" data-id="element-4994">
              <div className="flex justify-between items-center mb-2" data-id="element-4995">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4996">
                  C. Optional Tags – Toggle-Based Configuration
                </h3>
                <button onClick={() => toggleSection('optional')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-4997">
                  {expandedSections.optional ? <ChevronDownIcon className="h-5 w-5" data-id="element-4998" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-4999" />}
                </button>
              </div>
              {expandedSections.optional && <div className="space-y-4" data-id="element-5000">
                  {optionalTags.map(tag => <div key={tag.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4" data-id="element-5001">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5002">
                        <div className="mb-2 md:mb-0" data-id="element-5003">
                          <div className="flex items-center" data-id="element-5004">
                            <span className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2" data-id="element-5005">
                              Tag {tag.id}
                            </span>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5006">
                              {tag.name}
                            </h4>
                            <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-1 cursor-help" title={tag.description} data-id="element-5007" />
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-5008">
                          <button type="button" className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => toggleOptionalTag(tag.id)} disabled={(tag.id === '54' || tag.id === '53') && includeAmount} style={{
                      backgroundColor: tag.enabled ? '#3B82F6' : '#E5E7EB',
                      opacity: (tag.id === '54' || tag.id === '53') && includeAmount ? 0.5 : 1
                    }} data-id="element-5009">
                            <span className={`${tag.enabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`} data-id="element-5010"></span>
                          </button>
                          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-5011">
                            {tag.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                      {tag.enabled && <div className="mt-4" data-id="element-5012">
                          <input type="text" value={tag.value} onChange={e => handleOptionalTagChange(tag.id, e.target.value)} className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-800" placeholder={`Enter ${tag.name}`} disabled={(tag.id === '54' || tag.id === '53') && includeAmount} data-id="element-5013" />
                          {(tag.id === '54' || tag.id === '53') && includeAmount && <p className="mt-1 text-xs text-yellow-500" data-id="element-5014">
                                This value is controlled by the amount
                                configuration above
                              </p>}
                        </div>}
                    </div>)}
                </div>}
            </div>
            {/* Additional Data Fields (Tag 62) */}
            <div className="mb-6" data-id="element-5015">
              <div className="flex justify-between items-center mb-2" data-id="element-5016">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5017">
                  Additional Data Fields (Tag 62)
                </h3>
                <button onClick={() => toggleSection('additionalData')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-5018">
                  {expandedSections.additionalData ? <ChevronDownIcon className="h-5 w-5" data-id="element-5019" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-5020" />}
                </button>
              </div>
              {expandedSections.additionalData && <div className="space-y-4" data-id="element-5021">
                  {additionalDataFields.map(field => <div key={field.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4" data-id="element-5022">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-5023">
                        <div className="mb-2 md:mb-0" data-id="element-5024">
                          <div className="flex items-center" data-id="element-5025">
                            <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2" data-id="element-5026">
                              Field 62.{field.id}
                            </span>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5027">
                              {field.name}
                            </h4>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-5028">
                          <button type="button" className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => toggleAdditionalDataField(field.id)} style={{
                      backgroundColor: field.enabled ? '#3B82F6' : '#E5E7EB'
                    }} data-id="element-5029">
                            <span className={`${field.enabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`} data-id="element-5030"></span>
                          </button>
                          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-5031">
                            {field.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                      {field.enabled && <div className="mt-4" data-id="element-5032">
                          <input type="text" value={field.value} onChange={e => handleAdditionalDataFieldChange(field.id, e.target.value)} className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-800" placeholder={`Enter ${field.name}`} data-id="element-5033" />
                        </div>}
                    </div>)}
                </div>}
            </div>
            {/* Tag 63 – CRC Handling */}
            <div className="mb-6" data-id="element-5034">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4" data-id="element-5035">
                <div className="flex" data-id="element-5036">
                  <InfoIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" data-id="element-5037" />
                  <div data-id="element-5038">
                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-5039">
                      Tag 63 – CRC Handling
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400" data-id="element-5040">
                      <p data-id="element-5041">
                        The CRC (Cyclic Redundancy Check) is automatically
                        calculated and appended to the QR code. This ensures
                        data integrity and is required by the EMVCo standard.
                      </p>
                      <p className="mt-2" data-id="element-5042">
                        Current CRC value:{' '}
                        <code className="font-mono bg-yellow-100 dark:bg-yellow-900/30 px-1 py-0.5 rounded" data-id="element-5043">
                          {crc}
                        </code>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          {/* Section 4: Save & Issue */}
          <Card data-id="element-5044">
            <h2 className="text-lg font-medium mb-4" data-id="element-5045">Save & Issue</h2>
            <div className="flex flex-wrap gap-4" data-id="element-5046">
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={() => {
              // Preview QR code
              alert('QR Code preview is displayed on the right panel');
            }} data-id="element-5047">
                <EyeIcon className="h-4 w-4 mr-2" data-id="element-5048" />
                Preview QRC
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" onClick={() => {
              // Save QR code
              alert('QR Code saved to merchant profile');
            }} data-id="element-5049">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-5050" />
                Save QRC to Merchant Profile
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => {
              // Generate bulk QR codes
              alert('Bulk QR Codes will be generated for all tills');
            }} data-id="element-5051">
                <FileTextIcon className="h-4 w-4 mr-2" data-id="element-5052" />
                Generate Bulk for All Tills
              </button>
            </div>
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-5053">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5054">
                An audit log entry will be created when you save or update a QR
                code.
              </p>
            </div>
          </Card>
        </div>
        {/* QR Preview Panel */}
        <div className="lg:col-span-1" data-id="element-5055">
          <div className="sticky top-6" data-id="element-5056">
            <Card data-id="element-5057">
              <h2 className="text-lg font-medium mb-4" data-id="element-5058">
                QR Preview & Branding
              </h2>
              {qrError ? <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-4" data-id="element-5059">
                  <div className="flex" data-id="element-5060">
                    <XIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" data-id="element-5061" />
                    <div data-id="element-5062">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-5063">
                        Error Generating QR Code
                      </h3>
                      <div className="mt-2 text-sm text-red-700 dark:text-red-400" data-id="element-5064">
                        <p data-id="element-5065">{qrError}</p>
                      </div>
                    </div>
                  </div>
                </div> : <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-6 flex flex-col items-center" data-id="element-5066">
                  <div data-id="element-5067">
                    <QRCode id="qr-code" value={qrCodeValue || 'https://example.com'} size={200} level="H" includeMargin={true} className="mx-auto" data-id="element-5068" />
                  </div>
                  {/* Merchant details below QR code instead of overlaid */}
                  <div className="mt-3 text-center" data-id="element-5069">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-5070">
                      {merchantInfo.merchantName}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-5071">
                      TILL: {merchantInfo.tillId}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center space-x-3" data-id="element-5072">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6" data-id="element-5073" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/200px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6" data-id="element-5074" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/UnionPay_logo.svg/200px-UnionPay_logo.svg.png" alt="UnionPay" className="h-6" data-id="element-5075" />
                    <div className="bg-green-600 text-white text-xs font-bold px-2 rounded flex items-center" data-id="element-5076">
                      RAAST
                    </div>
                  </div>
                  <div className="mt-6 w-full space-y-3" data-id="element-5077">
                    <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={downloadQR} data-id="element-5078">
                      <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5079" />
                      Download QR PNG
                    </button>
                    <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={downloadMetadata} data-id="element-5080">
                      <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5081" />
                      Download Metadata JSON
                    </button>
                  </div>
                  {includeAmount && amount && <div className="mt-4 w-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3" data-id="element-5082">
                      <p className="text-sm text-blue-800 dark:text-blue-300 text-center" data-id="element-5083">
                        Fixed amount:{' '}
                        <strong data-id="element-5084">
                          {currency} {amount}
                        </strong>
                      </p>
                    </div>}
                </div>}
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400" data-id="element-5085">
                <p data-id="element-5086">
                  This QR code is compliant with EMVCo QR Code Specification for
                  Payment Systems.
                </p>
                <p className="mt-2" data-id="element-5087">
                  Scan with any compatible payment app to test.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>;
};
export default QRGenerator;